import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  DocumentReference,
  DocumentSnapshot,
  limit,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { m } from "framer-motion";
import Link from "next/link";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCollection } from "react-firebase-hooks/firestore";
import { BiCalendarWeek } from "react-icons/bi";
import { HiUsers } from "react-icons/hi";
import { IoMdDownload } from "react-icons/io";
import ReactTooltip from "react-tooltip";
import { db } from "../../firebase";
import {
  createPack,
  createPackLink,
  deleteTrackDb,
  docToUiTrack,
  downloadPack,
  generatePrivateLink,
  getNextDateOfWeekday,
  getTrack,
  getUser,
  Pack,
  UITrack,
  updatePack,
  User,
} from "../../firebase/firestore";
import { useAuth } from "../../hooks/useAuth";
import {
  FileUpload,
  FileUploadMap,
  FirestoreTrack,
  TrackUploadContextProvider,
  useStorageUpload,
} from "../../hooks/useUpload";
import { CheckboxSetting } from "../../pages/settings";
import { mutateWhere } from "../../utils/arrays";
import { randomString } from "../../utils/string";
import Button from "../inputs/Button";
import { HorizontalTabs } from "../inputs/HorizontalTabs";
import AppModal from "../layout/AppModal";
import { Copyable } from "../misc/Copyable";
import SpotLoader from "../misc/SpotLoader";
import { SectionTitle } from "../misc/Typography";
import TrackUpload from "../upload/TrackUpload";
import { PackShareSection } from "./PackShareSection";
import PackTitle from "./PackTitle";
import PackTracks from "./PackTracks";
import PackViews from "./PackViews";

const DownloadButton = ({ onDownload, ...rest }) => {
  const [loading, setLoading] = useState(false);
  const download = useCallback(async () => {
    setLoading(true);
    try {
      await onDownload();
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [setLoading, onDownload]);
  return (
    <div {...rest}>
      {loading ? (
        <SpotLoader className={"w-full h-full"} />
      ) : (
        <IoMdDownload
          className={`transition-opacity opacity-40 cursor-pointer hover:opacity-100 w-full h-full`}
          onClick={download}
        />
      )}
    </div>
  );
};

const DEBUG = true;
const debugLog = (...args) => DEBUG && console.log("PackPage: ", ...args);
export type PackPageProps = {
  pack: Pack;
  packRef: DocumentReference;
  packSnapshot: DocumentSnapshot;
  setPackRef: (newRef: DocumentReference) => any;
  canEdit: boolean;
  creator: User;
  onlyTracks?: boolean;
  customAddTracksMessage?: string;
  // we pass collective user id in here to let users create packs in collectives
  creatorUid?: string;
};

function _PackPage({
  pack,
  packRef,
  packSnapshot,
  creator,
  setPackRef,
  canEdit,
  customAddTracksMessage,
  onlyTracks,
  creatorUid,
  ...rest
}: PackPageProps) {
  const { user } = useAuth();
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [tracks, setTracks] = useState<UITrack[]>([]);

  const [loadedInitialTracks, setLoadedInitialTracks] = useState(false);
  const { setPack, fileUploads, cancelUpload, addUploadListener, updateFileUpload } = useStorageUpload();

  const lastPackUpdate = useRef(null);

  // load references from pack.tracks into tracks array
  useEffect(() => {
    // console.log('setting pack', setPack)
    // if (setPack !== null && typeof setPack === "function") setPack({...pack, ref: packRef})
    (async () => {
      try {
        if (pack && lastPackUpdate.current !== pack?.date_modified?.toDate()) {
          debugLog(
            "pack updated, loading tracks",
            `last modified date: ${lastPackUpdate.current}), new modified date: ${pack.date_modified.toDate()})`
          );
          lastPackUpdate.current = pack.date_modified.toDate();
          // if (pack?.tracks?.length)

          const newPackTracks = await Promise.all(
            pack.tracks.map(async (trackRef) => {
              const trackCreator = await getTrackAuthor(trackRef);
              return await getTrack(trackRef, trackCreator);
            })
          );

          let currentlyUploadingTracks = [...tracks].filter((track) => track.file && !track.ref);
          setTracks([...newPackTracks, ...currentlyUploadingTracks]);
          setLoadedInitialTracks(true);
        }
      } catch (e) {
        console.error(e);
        console.log(pack, tracks);
        throw e;
      }
    })();
  }, [pack]);

  const getTrackAuthor = (trackRef: DocumentReference) => getUser(trackRef.path.split("/")[1]);
  // todo: make padding collapsable for responsiveness
  // todo: make "name your pack" cta not visible simultaneously with "select tracks" cta
  const onDoneSelecting = async (newSelected: (FirestoreTrack | FileUpload)[]) => {
    if (!canAdd) return;

    // create pack if first time selecting
    debugLog("onDoneSelecting", newSelected);

    // fill track objects with creator attributes
    newSelected = await Promise.all(
      newSelected.map(async (track) => {
        return {
          ...track,
          track: {
            ...track.track,
            // if track is a file upload OR this pack is just being created, track creator is bound to be the user
            // warning: this is not true if the pack is being created from a collective track source (not yet possible)
            // todo: (once collective track sources are possible) make sure to get the track creator from the track source
            // @ts-ignore
            creator: pack && !track.file ? await getTrackAuthor(track.ref) : user,
          },
        };
      })
    );
    let _packRef = packRef;
    if (!packRef) {
      _packRef = await createPack(creatorUid || user.uid);
      setPackRef(_packRef);
    }

    console.log("selecting new tracls", newSelected, composeTracksArr(newSelected));

    setPack({ ...pack, tracks: composeTracksArr(newSelected) });

    const newFsTracks = composeTracksArr(newSelected);
    updatePack(_packRef, { tracks: newFsTracks });

    setTracks(newSelected);
    setSelectorOpen(false);
  };

  // subscribe to firestore tracks
  useTrackUpdater(tracks, updateFileUpload, setTracks, fileUploads, { ...pack, ref: packRef });

  // const generateActions = useCallback((uiTrack: UITrack) => {
  //   return [
  //     {title: 'Remove', cb: () => setTracks(oldTracks => oldTracks.filter(oT => oT.track.id !== uiTrack.track.id))},
  //     {title: 'Remove', cb: () => setTracks(oldTracks => oldTracks.filter(oT => oT.track.id !== uiTrack.track.id))},
  //   ]
  // }, [])
  const [publicLinkSnapshot, pubLinkLoading, pubLinkError] = useCollection(
    canEdit &&
      packRef &&
      query(collection(db, `users/${user.uid}/pack_links`), where("pack_id", "==", packRef.id), limit(1)),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );
  const publicLink = useMemo(
    () => publicLinkSnapshot && (publicLinkSnapshot.docs.length === 0 ? null : publicLinkSnapshot.docs[0]?.id || null),
    [publicLinkSnapshot]
  );
  const copyPublicLink = useCallback(async () => {
    if (pubLinkLoading) return;

    if (!publicLink)
      await navigator.clipboard.writeText(
        composePublicLink(
          isCollective ? creator : user,
          await createPackLink(isCollective ? creator?.id : user.uid, packRef)
        )
      );
    else await navigator.clipboard.writeText(composePublicLink(user, publicLink));
  }, [pubLinkLoading, publicLink, user, packRef, pack]);

  const copyPrivateLink = useCallback(async () => {
    if (!pack) return;

    if (!pack.private_link) {
      let generatedTitle = `pack-${randomString(8)}`;
      let newLink = await generatePrivateLink(user.uid, generatedTitle);
      await updatePack(packRef, { title: generatedTitle, private_link: newLink, tracks: composeTracksArr(tracks) });
      await navigator.clipboard.writeText(composePrivateLink(user, newLink.value));
    } else {
      await navigator.clipboard.writeText(composePrivateLink(user, pack.private_link.value));
    }
  }, [pack, user, packRef]);

  const onDeleteTrack = async (uiTrack: UITrack) => {
    //@ts-ignore
    if (uiTrack.ref) {
      // @ts-ignore
      if (pack.tracks.map((t) => t.id).includes(uiTrack.ref.id)) {
        // pack.tracks.filter((packTrack) => packTrack.path !== uiTrack.track.path)
        //@ts-ignore
        await updatePack(packRef, { tracks: arrayRemove(uiTrack.ref) });
      } else {
        // throw new Error("Trying to delete a track which is not in the pack")
      }
    } else {
      // FileUpload track

      console.log("deleting uploading track");
      await deleteTrackDb(uiTrack.track.path);
      //@ts-ignore
      await cancelUpload(uiTrack);
    }

    setTracks((tracks) => tracks.filter((t) => t.track.id !== uiTrack.track.id));
  };

  const releaseDate = useMemo(() => {
    if (pack?.weekly && pack?.next_weekly && user.weeklies?.sendout_day) {
      return getNextDateOfWeekday(user.weeklies.sendout_day);
    }
    return null;
  }, [pack, user]);

  const showSelector = !pack || (loadedInitialTracks && selectorOpen);

  const isCollective = pack?.creator?.isCollective;
  const userIsAdmin = pack?.creator?.admins?.includes(user.uid);
  const userIsMember = pack?.creator?.members?.includes(user.uid);
  const [showAllTracks, setShowAllTracks] = useState(true);
  const canAdd = canEdit || (isCollective && userIsMember && pack.submissionsOpen);
  return (
    <div
      className={`flex pb-16 justify-center items-center container m-auto px-4 md:px-2 w-full flex-col ${
        !onlyTracks && "min-h-screen"
      } pt-12`}
    >
      {!onlyTracks && (
        <div className="mb-14 w-full">
          {(!canEdit || !showSelector || pack?.title) && (
            <PackTitle
              value={pack?.title || (canEdit ? null : "Untitled pack")}
              editable={canEdit}
              setValue={updateTitle}
              className={`${pack?.title || canEdit ? "" : "opacity-30"}`}
              subtitle={
                creator && (
                  <span>
                    by @{creator?.nickname}
                    {creator.isCollective ? " (collective)" : ""}
                  </span>
                )
              }
              icons={
                <div className="opacity-30 group-hover:opacity-100 transition-opacity flex ml-2">
                  <DownloadButton className={" w-6 h-6"} onDownload={() => downloadPack(pack, tracks)} />
                </div>
              }
            />
          )}
          {pack?.weekly && <NextWeeklyDate pack={pack} releaseDate={releaseDate} nextWeeks={pack?.next_weekly} />}

          <div className="flex mt-1 flex-col items-center justify-center">
            {(canEdit || userIsMember) && (!showSelector || tracks?.length > 0) && (
              <PackViews pack={pack} packRef={packRef} />
            )}
          </div>
        </div>
      )}
      {(canEdit || (isCollective && userIsMember)) && (
        <AppModal isOpen={showSelector} onRequestClose={() => pack && setSelectorOpen(false)} dimBg={true}>
          <TrackUpload initSelected={tracks} onDoneSelecting={onDoneSelecting} />
        </AppModal>
      )}

      {/* toggle between all tracks and picked tracks */}
      {/* {isCollective && userIsAdmin && (
        <HorizontalTabs
          className="w-96"
          tabs={["All tracks", "Picked tracks"]}
          selectedTabIdx={showAllTracks ? 0 : 1}
          setSelectedTabIdx={(newIdx) => setShowAllTracks(newIdx === 0)}
        />
      )} */}

      <PackTracks
        customAddTracksMessage={customAddTracksMessage}
        canEdit={canEdit}
        canSelect
        tracks={tracks}
        setTracks={setTracks}
        creator={creator}
        canAdd={!showSelector && canAdd}
        cantAddMessage={userIsMember && <div>Submissions to this pack are closed</div>}
        canEditOwnTracks={!canEdit && isCollective && userIsMember && canAdd}
        onDelete={onDeleteTrack}
        onAdd={() => canAdd && setSelectorOpen(true)}
        showTrackAuthors={pack?.creator?.isCollective}
        pack={pack}
      />
      {!onlyTracks && !showSelector && canEdit && (
        <PackShareSection pack={pack} packRef={packRef} copyPrivateLink={copyPrivateLink} />
      )}

      {!onlyTracks && !showSelector && canEdit && tracks?.length !== 0 && pack && packRef && (
        <div className="flex z-20 p-2 w-full justify-end max-w-2xl">
          <Copyable
            data-tip="Anyone with this link can view the pack without signing in."
            className="h-9 cursor-pointer hover:bg-opacity-20 transition-all w-min mr-2 hover:bg-white rounded-md text-sm items-center bg-opacity-10 flex p-1 px-2 pr-3 ml-3 "
            onClick={copyPublicLink}
          >
            <div className=" whitespace-nowrap">Copy public link</div>
          </Copyable>
          {/* <Button onClick={publish} primary={!pack?.published} secondary={pack?.published} data-tip="Nobody sees the pack if it's not published">{pack?.published ? "Unpublish" : "Publish"}</Button> */}
          <ReactTooltip delayShow={300} place="bottom" effect="solid" />{" "}
          <ReactTooltip delayShow={600} place="bottom" effect="solid" />
        </div>
      )}

      {isCollective && userIsAdmin && (
        <div className="m-auto w-full max-w-2xl mt-6">
          <div className="flex items-center mb-2  text-3xl">
            <HiUsers className="mr-2" /> Collective settings
          </div>
          {
            <CheckboxSetting
              label="Submissions open"
              className="max-w-full"
              style={{ width: "100%" }}
              subLabel={<span>Allow your collective members to add their tracks to this pack.</span>}
              value={pack.submissionsOpen}
              changeValue={() => updatePack(packRef, { submissionsOpen: !pack?.submissionsOpen })}
            />
          }
        </div>
      )}
    </div>
  );

  async function updateTitle(title: string): Promise<any> {
    // const newLink = await generatePrivateLink(user.uid, title);
    await updatePack(packRef, { title });
    // window.history.pushState(null, "", `/@${user.nickname}/${newLink.value}`)
  }

  function composeTracksArr(newSelected: any) {
    return newSelected.filter((t) => t.ref).map((t) => doc(db, t.track.path));
  }
}

export function userHasCustomLinks(user: User) {
  return ["member", "platinum"].includes(user?.plan);
}

export function composePrivateLink(user: User, privateLink: string, forcePathLink?: boolean): string {
  return `${window.location.protocol}//${window.location.host}/@${user.nickname}/${privateLink}`;

  // return !forcePathLink && userHasCustomLinks(user)
  //   ? `${window.location.protocol}//${user.nickname}.${window.location.host.replace("www.", "")}/${privateLink}`
  //   : `${window.location.protocol}//${window.location.host}/@${user.nickname}/${privateLink}`;
}
export function composePublicLink(user: User, publicLink: string, forceDirect = false): any {
  return `${window.location.protocol}//${window.location.host}/@${user.nickname}/shared/${publicLink}`;

  // removed custom subdomain links for now (make loading sluggish)
  // return !forceDirect && userHasCustomLinks(user)
  //   ? `${window.location.protocol}//${user.nickname}.${window.location.host.replace("www.", "")}/shared/${publicLink}`
  //   : `${window.location.protocol}//${window.location.host}/@${user.nickname}/shared/${publicLink}`;
}

// keeps firestore snapshot subscriptions up-to-date with tracks array
export function useTrackUpdater(
  tracks: UITrack[],
  updateFileUpload: (track: UITrack) => any,
  setTracks: React.Dispatch<React.SetStateAction<UITrack[]>>,
  fileUploads: FileUploadMap,
  pack?: Pack,
  onlyFirestore?: boolean
) {
  const firestoreUnsubs = useRef<{ [firestorePath: string]: () => any }>({});
  const { user } = useAuth();
  useEffect(() => {
    if (!setTracks) return;
    //@ts-ignore
    tracks?.forEach((uiTrack: UITrack) => {
      // @ts-ignore
      if (uiTrack?.ref) {
        // @ts-ignore
        let uiFirestoreTrack: FirestoreTrack = uiTrack;
        if (!firestoreUnsubs.current[uiFirestoreTrack.track.path]) {
          // haven't subscribed to this track yet
          if (
            pack &&
            uiTrack.justUploaded &&
            (!pack?.tracks || !pack.tracks.map((r) => r.id).includes(uiFirestoreTrack.ref.id))
          ) {
            // track just uploaded and not yet in pack
            // add to pack
            updatePack(pack.ref, { tracks: arrayUnion(uiFirestoreTrack.ref) });
            debugLog("updated pack tracks", pack?.tracks);
          } else if (pack && (!pack?.tracks || !pack.tracks.map((r) => r.id).includes(uiFirestoreTrack.ref.id))) {
            // track not just-uploaded (=added already uploaded) and not yet in pack
            // add to pack
            updatePack(pack.ref, { tracks: arrayUnion(uiFirestoreTrack.ref) });
            debugLog("updated pack tracks", pack?.tracks);
          }
          debugLog(`attaching listener for ${uiFirestoreTrack.track.title}`);

          firestoreUnsubs.current[uiFirestoreTrack.track.path] = onSnapshot(
            uiFirestoreTrack.ref,
            async (newTrackDoc) => {
              const newUiTrack = {
                ...uiFirestoreTrack,
                // load real track creator in case pack creator != track creator (in case of shared packs)
                ...docToUiTrack(
                  newTrackDoc,
                  pack ? (await getUser(uiFirestoreTrack.ref.path.split("/")[1])) || pack.creator : user
                ),
              };

              updateFileUpload(newUiTrack);
              setTracks((oldTracks) => {
                return oldTracks.map((oldTrack) => {
                  if (oldTrack.track.path === uiTrack.track.path) {
                    // console.log(`replacing ${oldTrack.track.title} (${oldTrack.track.analyze_status}) with ${newUiTrack.track.analyze_status} `);
                    return newUiTrack;
                  }
                  return oldTrack;
                });
              });
            }
          );
        }
      }
    });

    // unsubscribe from removed tracks
    Object.entries(firestoreUnsubs.current).map(([firestorePath, unsub]) => {
      if (tracks.findIndex((t) => t.track.path === firestorePath) === -1) {
        unsub();
        delete firestoreUnsubs.current[firestorePath];
      }
    });
  }, [setTracks, tracks, fileUploads]);

  // apply updates from fileUploads to selected tracks
  useEffect(() => {
    if (onlyFirestore) return;
    setTracks((tracks) => {
      let newTracks = [...tracks];
      Object.values(fileUploads).forEach((uiTrack) => {
        // @ts-ignore
        let fileUpload: FileUpload = uiTrack;
        // replace selected tracks
        newTracks = newTracks.map((oldTrack) =>
          oldTrack.track.path === fileUpload.track.path ? fileUpload : oldTrack
        );
      });
      return newTracks;
    });
  }, [setTracks, fileUploads]);
}
export function PackPage({ ...rest }) {
  //@ts-ignore
  return (
    <TrackUploadContextProvider>
      {/* 
        // @ts-ignore */}
      <_PackPage {...rest} />
    </TrackUploadContextProvider>
  );
}

export const NextWeeklyDate = ({ pack, releaseDate, nextWeeks = false, hideWeeklyNotice = false, ...rest }) => (
  <div className="flex text-sm items-center justify-center text-light-gray text-center w-full">
    <BiCalendarWeek />
    &nbsp;
    {!hideWeeklyNotice && (
      <Link href={pack.creator.isCollective ? `/collective/${pack.creator.id}/weeklies` : "/weeklies"}>
        <span className="hover:underline cursor-pointer">{pack.next_weekly ? "Next weekly pack" : "Weekly pack"}</span>
      </Link>
    )}
    {releaseDate && nextWeeks && !hideWeeklyNotice && ", "}
    {releaseDate && `sending out on ${releaseDate.toDateString()} at 00:00`}
  </div>
);
